import React, { useState } from 'react';
import axios from '../lib/axios';
import { useNavigate } from 'react-router-dom';

const DocumentToTemplateConverter = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Upload, 2: Review Suggestions, 3: Download
  const [selectedFile, setSelectedFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [staticSections, setStaticSections] = useState([]);
  const [converting, setConverting] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.docx')) {
        alert('Please select a DOCX file');
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setAnalyzing(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post('/api/templates/analyze-document', formData, {
      });

      setAnalysis(response.data.analysis);
      setSuggestions(
        response.data.analysis.suggestions.map(s => ({
          ...s,
          approved: true, // Auto-approve all by default
          editedPlaceholder: s.placeholder
        }))
      );
      setStaticSections(
        response.data.analysis.static_sections.map(s => ({
          ...s,
          approved: true // Auto-approve static sections
        }))
      );
      setStep(2);

    } catch (error) {
      console.error('Analysis failed:', error);
      alert('Failed to analyze document: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleApproval = (index) => {
    setSuggestions(prev => {
      const updated = [...prev];
      updated[index].approved = !updated[index].approved;
      return updated;
    });
  };

  const updatePlaceholder = (index, newName) => {
    setSuggestions(prev => {
      const updated = [...prev];
      updated[index].editedPlaceholder = newName;
      return updated;
    });
  };

  const toggleStaticSection = (index) => {
    setStaticSections(prev => {
      const updated = [...prev];
      updated[index].approved = !updated[index].approved;
      return updated;
    });
  };

  const handleConvert = async () => {
    const approvedSuggestions = suggestions
      .filter(s => s.approved)
      .map(s => ({
        field_type: s.field_type,
        original_value: s.original_value,
        placeholder: s.editedPlaceholder,
        context: s.context,
        location: s.location
      }));

    const approvedStaticLocations = staticSections
      .filter(s => s.approved)
      .map(s => s.location);

    if (approvedSuggestions.length === 0) {
      alert('Please approve at least one placeholder suggestion');
      return;
    }

    setConverting(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('approved_suggestions', JSON.stringify(approvedSuggestions));
      formData.append('approved_static_sections', JSON.stringify(approvedStaticLocations));

      const response = await axios.post('/api/templates/convert-to-template', formData, {
        responseType: 'blob',
      });

      // Download the template
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_${selectedFile.name}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      alert(`Template created successfully!\n\n- ${approvedSuggestions.length} placeholders (blue text)\n- ${approvedStaticLocations.length} static sections (yellow highlight)\n\nThe file has been downloaded.`);
      setStep(3);

    } catch (error) {
      console.error('Conversion failed:', error);
      alert('Failed to convert document: ' + (error.response?.data?.detail || error.message));
    } finally {
      setConverting(false);
    }
  };

  const groupedSuggestions = suggestions.reduce((acc, suggestion) => {
    if (!acc[suggestion.field_type]) {
      acc[suggestion.field_type] = [];
    }
    acc[suggestion.field_type].push(suggestion);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/templates')}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
          >
            <span className="mr-2">←</span> Back to Templates
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Convert Document to Template</h1>
          <p className="text-gray-600 mt-2">
            Upload a filled legal opinion document and AI will automatically identify fields and suggest placeholders
          </p>
        </div>

        {/* Step Indicator */}
        <div className="mb-8 flex items-center justify-center">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                1
              </div>
              <span className="ml-2 font-medium">Upload Document</span>
            </div>
            <div className="w-16 h-1 bg-gray-300"></div>
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                2
              </div>
              <span className="ml-2 font-medium">Review Suggestions</span>
            </div>
            <div className="w-16 h-1 bg-gray-300"></div>
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                3
              </div>
              <span className="ml-2 font-medium">Download Template</span>
            </div>
          </div>
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Step 1: Upload Your Filled Document</h2>
            <p className="text-gray-600 mb-6">
              Upload a completed legal opinion document (DOCX format). Our AI will analyze it and identify fields that should become placeholders.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select DOCX File
              </label>
              <input
                type="file"
                accept=".docx"
                onChange={handleFileSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {selectedFile && (
                <p className="text-sm text-green-600 mt-2">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            <button
              onClick={handleAnalyze}
              disabled={!selectedFile || analyzing}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              {analyzing ? 'Analyzing Document...' : 'Analyze Document'}
            </button>
          </div>
        )}

        {/* Step 2: Review Suggestions */}
        {step === 2 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">
              Step 2: Review Suggestions
            </h2>
            <p className="text-gray-600 mb-6">
              Review placeholders (dynamic fields) and static text sections (reusable legal clauses).
            </p>

            <div className="mb-6 flex items-center space-x-4 bg-blue-50 p-4 rounded">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-600 rounded"></div>
                <span className="text-sm font-medium">Blue = Placeholders ({suggestions.filter(s => s.approved).length})</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-yellow-300 rounded"></div>
                <span className="text-sm font-medium">Yellow = Static Text ({staticSections.filter(s => s.approved).length})</span>
              </div>
            </div>

            {/* Static Text Sections */}
            {staticSections.length > 0 && (
              <div className="mb-6 border rounded-lg p-4 bg-yellow-50">
                <h3 className="font-semibold text-lg mb-3">📝 Static Text Sections (Reusable Legal Clauses)</h3>
                <p className="text-sm text-gray-600 mb-3">
                  These sections will be highlighted in yellow and kept as standard text in your template.
                </p>
                
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {staticSections.map((section, idx) => (
                    <div key={idx} className="flex items-start space-x-3 bg-white p-3 rounded border">
                      <input
                        type="checkbox"
                        checked={section.approved}
                        onChange={() => toggleStaticSection(idx)}
                        className="mt-1 h-5 w-5 text-yellow-600"
                      />
                      <div className="flex-1">
                        <div className="text-sm text-gray-700 italic">
                          "{section.preview}"
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Location: {section.location}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dynamic Placeholders */}
            {/* Dynamic Placeholders */}
            <div className="space-y-6">
              <h3 className="font-semibold text-lg">🔵 Dynamic Placeholders (Variable Fields)</h3>
              {Object.entries(groupedSuggestions).map(([fieldType, items]) => (
                <div key={fieldType} className="border rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-3 capitalize">{fieldType.replace('_', ' ')}</h3>
                  
                  <div className="space-y-3">
                    {items.map((suggestion, idx) => (
                      <div key={idx} className="flex items-start space-x-3 bg-gray-50 p-3 rounded">
                        <input
                          type="checkbox"
                          checked={suggestion.approved}
                          onChange={() => toggleApproval(suggestions.indexOf(suggestion))}
                          className="mt-1 h-5 w-5 text-blue-600"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-sm font-medium text-gray-700">Original:</span>
                            <code className="bg-yellow-100 px-2 py-1 rounded text-sm">{suggestion.original_value}</code>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700">Placeholder:</span>
                            <input
                              type="text"
                              value={suggestion.editedPlaceholder}
                              onChange={(e) => updatePlaceholder(suggestions.indexOf(suggestion), e.target.value)}
                              disabled={!suggestion.approved}
                              className="px-2 py-1 border rounded text-sm flex-1 max-w-md disabled:bg-gray-200"
                            />
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Context: ...{suggestion.context}...
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-md hover:bg-gray-600 font-medium"
              >
                Back
              </button>
              <button
                onClick={handleConvert}
                disabled={converting || suggestions.filter(s => s.approved).length === 0}
                className="flex-1 bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
              >
                {converting ? 'Converting...' : `Create Template with ${suggestions.filter(s => s.approved).length} Placeholders`}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Download */}
        {step === 3 && (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="mb-6">
              <svg className="mx-auto h-16 w-16 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Template Created Successfully!</h2>
            <p className="text-gray-600 mb-6">
              Your template has been downloaded with:<br/>
              • <strong className="text-blue-600">{suggestions.filter(s => s.approved).length} placeholders</strong> (blue text)<br/>
              • <strong className="text-yellow-600">{staticSections.filter(s => s.approved).length} static sections</strong> (yellow highlight)
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/templates')}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 font-medium"
              >
                Go to Templates & Upload
              </button>
              <button
                onClick={() => {
                  setStep(1);
                  setSelectedFile(null);
                  setAnalysis(null);
                  setSuggestions([]);
                }}
                className="w-full bg-gray-500 text-white py-3 px-4 rounded-md hover:bg-gray-600 font-medium"
              >
                Convert Another Document
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentToTemplateConverter;
